import { CopyOutlined } from '@ant-design/icons';
import type { LazyQueryExecFunction, OperationVariables } from '@apollo/client';
import {
  Button,
  Col,
  Input,
  Layout,
  Modal,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import type { SortOrder } from 'antd/lib/table/interface';
import { format } from 'date-fns';
import { Key, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import type { EventCreated, StoredUser } from '../../../hooks/types';
import useLazyQueryEventTeamCreated from '../../../hooks/useLazyQueryEventTeamCreated';
import { useLoggedInUser } from '../../../hooks/useLoggedInUser';
import { useNotify } from '../../../hooks/useNotify';
import { Permissions, usePermission } from '../../../hooks/usePermission';
import useQueryAllTeams, {
  AllTeamsResult,
  allTeamsQueryName,
} from '../../../hooks/useQueryAllTeams';
import useQueryUsersByIdsLazy from '../../../hooks/useQueryUsersByIdsLazy';
import useReactivateTeam from '../../../hooks/useReactivateTeam';
import { onApolloError } from '../../../lib/graphql/helpers';
import {
  arrayWithoutDuplicates,
  firstOrSelf,
  useDebounce,
} from '../../../lib/utils';
import { asDateNumber } from '../../../lib/utils/date-time';
import { successMessage } from '../../../lib/utils/messages';
import rfsTheme from '../../../theme';
import Select from '../../AccessibleComponents/Select';
import routes, { routesWithPathParameter } from '../../App/routes';
import HeaderSection from '../../HeaderSection';
import PageLoadingSpinner from '../../PageLoadingSpinner';
import { Optional } from '../../Shared';
import TablePagination, {
  TableSearchProps,
  getTableSearchProps,
} from '../../TablePagination';
import TeamHistory, { TeamHistoryProps } from '../TeamHistory';
import {
  ArchiveStatus,
  NameAndCallback,
  TeamSortBy,
  TeamsToDisplay,
  archiveStatusSchema,
  recordStatusSchema,
  teamNameMaxWidth,
  teamTypes,
  teamsToDisplay,
} from '../types';

/**
 * The amount the teams in each page of team table
 */
const teamsPerPage = 20;

/**
 * Supply this as truthy to query all teams to query SOC teams only.
 */
const querySocTeamOnly = true;

/**
 * All non SOC team types for table team type filter
 */
const nonSocTeamType = teamTypes(true);

type IonAndCallback = {
  readonly notListed?: boolean;
  readonly name?: string | null;
  readonly teamId?: string | null;
} & {
  readonly onClick?: () => Promise<void> | undefined;
};

type TeamName = {
  readonly name: string;
  readonly teamId: string;
};

type TeamTableRow = {
  readonly key: string;
  readonly name: TeamName;
  readonly teamType: string;
  readonly ion: IonAndCallback;
  readonly roles: string;
  readonly dateCreated: Date;
  readonly status: NameAndCallback;
};

const { Title } = Typography;

/**
 * The text that should be displayed in the Team History modal
 * when no reason is provided for a status event
 * (create, archive, or reactivate).
 *
 * Because there is no facility for providing a reason for
 * creating a team, this will always be shown for the "Created" event.
 */
const noReasonProvided = '-';

/**
 * Determines the reason for a status event.
 * - If the reason is undefined, no reason exists.
 * - If the reason is 'predates_reason_column', the record was created
 *   before the reason column was added to the database.
 *
 * @param reason - The reason for the status event.
 * @returns The reason for the status event or `noReasonProvided` if no reason exists.
 */
const determineReason = (reason: string | undefined): string =>
  reason === undefined || reason === 'predates_reason_column'
    ? noReasonProvided
    : reason;

const tableColumnHeadings = (
  sortBy?: TeamSortBy
): ColumnsType<TeamTableRow> => {
  return [
    {
      title: 'Team Name',
      dataIndex: 'name',
      key: 'name',
      render: (value: TeamName) => (
        <Tooltip title={`Manage Roster for ${value.name}`}>
          <Link
            to={routesWithPathParameter(
              routes.teamRoster,
              'teamId',
              value.teamId
            )}
          >
            <p
              style={{
                color: rfsTheme.darkRfsRed,
                maxWidth: teamNameMaxWidth,
                textDecoration: 'underline',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {value.name}
            </p>
          </Link>
        </Tooltip>
      ),
      sorter: sortBy !== undefined ? true : undefined,
      sortOrder: sortBy !== undefined ? sortBy.name : undefined,
      sortDirections: sortBy !== undefined ? ['ascend', 'descend'] : undefined,
    },
    {
      title: 'Team Type',
      dataIndex: 'teamType',
      key: 'teamType',
    },
    {
      title: 'Internal Order Number',
      dataIndex: 'ion',
      key: 'ion',
      width: 150,
      render: (value: IonAndCallback) => (
        <>
          {value.name !== undefined &&
          value.name !== null &&
          value.teamId !== undefined &&
          value.teamId !== null &&
          value.notListed !== true ? (
            <Row id={`team-${value.teamId}-io-number`}>
              <Col span={20} style={{ alignSelf: 'center' }}>
                {value.name}
              </Col>
              <Col span={4}>
                <Tooltip title="Copy IO Number to clipboard">
                  <Button
                    style={{
                      background: 'transparent',
                      border: 'transparent',
                      paddingLeft: '0.25rem',
                    }}
                    aria-label={'Copy IO Number to clipboard'}
                    onClick={() =>
                      void navigator.clipboard.writeText(value.name ?? '')
                    }
                  >
                    <CopyOutlined />
                  </Button>
                </Tooltip>
              </Col>
            </Row>
          ) : (
            <div>
              {value.notListed === true ? 'Not Listed' : 'Not Applicable'}
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Rostered Roles',
      dataIndex: 'roles',
      key: 'roles',
    },
    {
      title: 'Date Created',
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: (value: Date) => format(value, 'dd-MM-yyyy'),
      sorter: sortBy !== undefined ? true : undefined,
      sortOrder: sortBy !== undefined ? sortBy.created_at : undefined,
      sortDirections: ['ascend', 'descend', 'ascend'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: NameAndCallback) => (
        <Tooltip title={'Team History'}>
          <Button
            onClick={value.onClick}
            style={{ color: rfsTheme.errorTextColour }}
          >
            <p>{value.name}</p>
          </Button>
        </Tooltip>
      ),
    },
  ];
};

const displayStatus = (unknownStatus: unknown): ArchiveStatus => {
  const maybeRecordStatus = recordStatusSchema.safeParse(unknownStatus);
  return maybeRecordStatus.success &&
    maybeRecordStatus.data === recordStatusSchema.Values.Deleted
    ? archiveStatusSchema.Values.Archived
    : archiveStatusSchema.Values.Active;
};

const tableRows = ({
  team,
  getTeamCreatedEvents,
  getUsers,
  onReactivate,
  setStatusModal,
  isSocTeamTable,
  permissions,
}: {
  readonly team: readonly AllTeamsResult[];
  readonly getTeamCreatedEvents: LazyQueryExecFunction<
    EventCreated,
    OperationVariables
  >;
  readonly getUsers: LazyQueryExecFunction<
    { user: StoredUser[] },
    OperationVariables
  >;
  readonly onReactivate: (teamId: string, reason: string) => Promise<void>;
  readonly setStatusModal: React.Dispatch<
    React.SetStateAction<{
      readonly open: boolean;
      readonly props: TeamHistoryProps | undefined;
    }>
  >;
  readonly isSocTeamTable: boolean;
  readonly permissions: Permissions;
}): readonly TeamTableRow[] => {
  return team.map((team) => ({
    key: team.id,
    name: {
      name: team.name,
      teamId: team.id,
    },
    teamType: team.team_type,
    ion: isSocTeamTable
      ? { notListed: true }
      : {
          name: team.internal_order_number,
          teamId: team.id,
          onClick: () =>
            team.internal_order_number !== undefined
              ? navigator.clipboard.writeText(team.internal_order_number)
              : undefined,
        },
    roles: team.team_role_assignments
      .map((roleAssignment) => roleAssignment.team_role.name)
      .join(', '),
    dateCreated: new Date(team.created_at),
    status: {
      name: displayStatus(team.record_status),
      // When the team row Status ("Active/Archived") button is clicked,
      // a History modal will be opened to show the team history
      // (i.e. when the team was created, archived, reactivated, etc.)
      onClick: async () => {
        // Determine who the authors of the status events were...
        // 1. Query for the ID of the user who created the team
        // 2. Accumulate all IDs of the users who have changed the team status
        // 3. Query for the display names of the users with these IDs

        // event_team_created lacks a foreign key to the team table,
        // so we need to query for it separately.
        const teamCreatedEvent = await getTeamCreatedEvents({
          variables: { teamId: team.id },
        }).then((e) => e.data?.event_team_created.at(0)?.event);

        // The user ids of the users who created the team and deleted/reactivated it
        const usersWhoHaveChangedTheTeamStatus = [
          ...(teamCreatedEvent === undefined
            ? []
            : [teamCreatedEvent.created_by]),
          ...team.event_team_deleted.map(({ event }) => event.created_by),
          ...team.event_team_reactivated.map(({ event }) => event.created_by),
        ] as const;

        // (table:event, column:created_by) lacks a foreign key to the user table,
        // so we need to query for the users separately.
        const users = await getUsers({
          variables: { ids: usersWhoHaveChangedTheTeamStatus },
        });

        // Use the retrieved user information to find the display name of each user
        const findUser = (id: string | undefined): string =>
          users.data?.user.find((user) => user.id === id)?.display_name ??
          'System';

        const statusModalProps = {
          teamName: team.name,
          events: [
            {
              name: 'Created',
              date: asDateNumber(team.created_at),
              user: findUser(teamCreatedEvent?.created_by),
              reason: determineReason(undefined),
            },
            ...team.event_team_deleted.map((event) => ({
              name: 'Archived',
              date: asDateNumber(event.event.created_at),
              user: findUser(event.event.created_by),
              reason: determineReason(event.reason),
            })),
            ...team.event_team_reactivated.map((event) => ({
              name: 'Reactivated',
              date: asDateNumber(event.event.created_at),
              user: findUser(event.event.created_by),
              reason: determineReason(event.reason),
            })),
          ],
          // If a user has permission to delete a team, they can also reactivate it
          allowChangeStatus: permissions.teamDelete,
          status: displayStatus(team.record_status),
          onReactivate: (reason: string) => onReactivate(team.id, reason),
        };

        setStatusModal({
          open: true,
          props: statusModalProps,
        });
      },
    },
  }));
};

/**
 * Renders a table of all all teams.
 * @returns a table of known teams, sorted by name in ascending order.
 */
const AllTeams = () => {
  const { notify } = useNotify();
  const { loggedInUser } = useLoggedInUser();
  const permissions = usePermission(undefined, loggedInUser);

  const [selectedTeamsToDisplay, setSelectedTeamsToDisplay] =
    useState<TeamsToDisplay>('Active Only');
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search);
  const [searchRows, setSearchRows] = useState<TableSearchProps>(
    getTableSearchProps(teamsPerPage, 1)
  );
  const [resultsPage, setResultsPage] = useState(1);
  const [sortBy, setSortBy] = useState<TeamSortBy>({
    name: undefined,
    created_at: 'descend',
  });
  const [filteredTeamTypes, setFilteredTeamTypes] = useState<
    readonly string[] | undefined
  >(undefined);

  const [statusModal, setStatusModal] = useState<{
    readonly open: boolean;
    readonly props: TeamHistoryProps | undefined;
  }>({
    open: false,
    props: undefined,
  });

  // Query SOC teams
  const { data: socTeamQueryResult } = useQueryAllTeams({
    querySoc: querySocTeamOnly,
    teamsToDisplay: selectedTeamsToDisplay,
    search: `%${debouncedSearch}%`,
    limit: searchRows.limit,
    offset: searchRows.offset,
    sortBy: undefined,
    filteredTeamType: undefined,
    onError: onApolloError(notify.error),
  });

  // Query non-SOC teams
  const { data: nonSocTeamQueryResult } = useQueryAllTeams({
    querySoc: !querySocTeamOnly,
    teamsToDisplay: selectedTeamsToDisplay,
    search: `%${debouncedSearch}%`,
    limit: searchRows.limit,
    offset: searchRows.offset,
    sortBy,
    filteredTeamType: filteredTeamTypes,
    onError: onApolloError(notify.error),
  });

  const [reactivateTeam] = useReactivateTeam(onApolloError(notify.error));
  const [getUsers] = useQueryUsersByIdsLazy();
  const [getTeamCreatedEvents] = useLazyQueryEventTeamCreated();

  const onPageChange = useCallback((pageNumber: number) => {
    setResultsPage(pageNumber);
    setSearchRows(getTableSearchProps(teamsPerPage, pageNumber));
  }, []);

  if (loggedInUser === undefined) {
    return <PageLoadingSpinner />;
  }

  const onReactivate = async (teamId: string, reason: string) => {
    await reactivateTeam({
      variables: { teamId, reason },
      onCompleted: () => {
        notify.success(successMessage.teamReactivated);
        setStatusModal({ ...statusModal, open: false });
      },
      refetchQueries: [allTeamsQueryName],
    });
  };

  const socTableRowData: readonly TeamTableRow[] = tableRows({
    team: socTeamQueryResult?.team ?? [],
    getTeamCreatedEvents,
    getUsers,
    onReactivate,
    setStatusModal,
    isSocTeamTable: true,
    permissions,
  });

  const nonSocTableRowData: readonly TeamTableRow[] = tableRows({
    team: nonSocTeamQueryResult?.team ?? [],
    getTeamCreatedEvents,
    getUsers,
    onReactivate,
    setStatusModal,
    isSocTeamTable: false,
    permissions,
  });

  const socDataId = socTableRowData.find(
    (team) => team.teamType === 'SOC'
  )?.key;

  const resetPage = () => {
    setResultsPage(1);
    setSearchRows(getTableSearchProps(teamsPerPage, 1));
  };

  const teamTypeFilterOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newFilteredTeamTypes: readonly string[] =
        filteredTeamTypes === undefined
          ? [e.target.value]
          : [...filteredTeamTypes, e.target.value];
      setFilteredTeamTypes(arrayWithoutDuplicates(newFilteredTeamTypes));
    } else {
      const newFilteredTeamTypes: readonly string[] | undefined =
        filteredTeamTypes?.filter((type) => type !== e.target.value);

      setFilteredTeamTypes(
        newFilteredTeamTypes === undefined || newFilteredTeamTypes.length === 0
          ? undefined
          : arrayWithoutDuplicates(newFilteredTeamTypes)
      );
    }
    resetPage();
  };

  const sorterOnChange = (sortKey?: Key, order?: SortOrder) => {
    if (sortKey === undefined) {
      setSortBy({
        name: undefined,
        created_at: undefined,
      });
      resetPage();
    } else if (sortKey === 'name') {
      setSortBy({
        name: order,
        created_at: undefined,
      });
      resetPage();
    } else if (sortKey === 'dateCreated') {
      setSortBy({
        name: undefined,
        created_at: order,
      });
      resetPage();
    }
  };

  const totalTeams =
    nonSocTeamQueryResult?.team_aggregate.aggregate?.count ?? 0;

  return (
    <>
      <Layout>
        <HeaderSection title="All Teams" newTeam={true} />

        <Space
          direction="vertical"
          size="small"
          style={{ display: 'flex', marginBottom: '20px' }}
        >
          <Row style={{ alignItems: 'center' }}>
            <Col style={{ width: '120px' }}>
              <label htmlFor="archived-teams-select">Teams to Display</label>
            </Col>
            <Col style={{ width: '180px' }}>
              <Select<TeamsToDisplay>
                options={teamsToDisplay}
                title={'archived-teams-select'}
                id={'archived-teams-select'}
                value={selectedTeamsToDisplay}
                onChange={(event) => {
                  resetPage();
                  setSelectedTeamsToDisplay(
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    event.currentTarget.value as TeamsToDisplay
                  );
                }}
                style={{ padding: '5px' }}
              />
            </Col>
          </Row>

          <Row style={{ alignItems: 'center' }}>
            <Col style={{ width: '120px' }}>
              <label htmlFor="search-team-input">Search Team</label>
            </Col>
            <Col style={{ width: '180px' }}>
              <Input
                id="search-team-input"
                value={search}
                placeholder="Enter team name here"
                onInput={(e) => {
                  setSearch(e.currentTarget.value);
                  resetPage();
                }}
              />
            </Col>
          </Row>
        </Space>

        <Optional show={socDataId !== undefined && resultsPage === 1}>
          <Title level={3}>State Operation Centre Team</Title>
          <Table
            id="soc-team-table"
            dataSource={socTableRowData}
            columns={tableColumnHeadings(undefined)}
            pagination={false}
            style={{ marginBottom: '10px' }}
          />
          <Title level={3}>Other Teams</Title>
        </Optional>

        <Row style={{ margin: '20px 0' }}>
          <span>Filter by Team Type: </span>
          <div className="team-type-checkbox-wrapper">
            {nonSocTeamType.map((teamType) => (
              <label key={teamType}>
                <input
                  className="team-type-checkbox"
                  type="checkbox"
                  value={teamType}
                  onChange={teamTypeFilterOnChange}
                />
                <span>{teamType}</span>
              </label>
            ))}
          </div>
        </Row>

        <Table
          id="non-soc-team-table"
          dataSource={nonSocTableRowData}
          columns={tableColumnHeadings(sortBy)}
          pagination={false}
          style={{ marginBottom: '10px' }}
          onChange={(_, _filters, sorter) => {
            sorterOnChange(
              firstOrSelf(sorter)?.columnKey,
              firstOrSelf(sorter)?.order
            );
          }}
        />

        <Optional
          show={
            nonSocTeamQueryResult?.team_aggregate.aggregate?.count === undefined
              ? false
              : nonSocTeamQueryResult.team_aggregate.aggregate.count >
                teamsPerPage
          }
        >
          <TablePagination
            currentPage={resultsPage}
            total={totalTeams}
            itemsPerPage={teamsPerPage}
            onPageChange={onPageChange}
          />
        </Optional>
      </Layout>

      <Modal
        destroyOnClose
        title={`Team History: ${statusModal.props?.teamName ?? 'Loading...'}`}
        open={statusModal.open}
        footer={null}
        onCancel={() => setStatusModal({ ...statusModal, open: false })}
        width={'95vw'}
        style={{ maxWidth: '900px' }}
      >
        {statusModal.props === undefined ? null : (
          <TeamHistory {...statusModal.props} />
        )}
      </Modal>
    </>
  );
};

export default AllTeams;
